// @flow
import { Download, EuroRounded, NavigateBefore, Percent } from "@mui/icons-material";
import SaveIcon from "@mui/icons-material/Save";
import {
	Button,
	DialogActions,
	FormControl, FormHelperText,
	FormLabel,
	ListSubheader,
	TextField,
	useMediaQuery,
	useTheme
} from "@mui/material";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid2";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import { v4 as uuidv4 } from "uuid";
import MenuItem from "@mui/material/MenuItem";
import { ErrorMessage, FormikProvider } from "formik";
import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { NumericFormat } from "react-number-format";
import { AuthenticatedFile } from "../../../../../components/custom/general/authenticatedLink/authenticatedFile";
import { AuthenticatedLink } from "../../../../../components/custom/general/authenticatedLink/authenticatedLink";
import FormBoxTitle from "../../../../../components/mui/app/formBoxTitle/formBoxTitle";
import Confirmation from "../../../../../components/mui/general/confirmation/confirmation";
import PrintButton from "../../../../../components/mui/general/printButton/printButton";
import { API, DiscountTypes } from "../../../../../helpers/constants";
import { downloadFile } from "../../../../../helpers/fileHandle";
import {
	calcItemsTotal,
	calcItemsTotalWithCharges,
	calcTotalWithDiscount,
	calcTotalWithMarkup
} from "../../../../../helpers/helpers";
import {
	formControlDefaults,
	numericFormatDefaults, numericFormatWithIconProps,
	textFieldDefaults, textFieldSelectDefaults
} from "../../../../../helpers/theme/defaultProps";
import useUserData from "../../../../../hooks/user/useUserData";
import useCustomerSOForm from "../../../hooks/useCustomerSOForm";
import useEmailSO from "../../../hooks/useEmailSO";
import { ISSUES_FORM } from "../../../locales/namespaces";
import Charges from "./charges";
import Items from "./items";
import Offers from "./offers";
import SendSOEmail from "./sendSOEmail";

type Props = {};

const totalSx = {
	display: 'flex',
	alignItems: 'flex-end',
	justifyContent: 'flex-end',
	background: '#f1f4ff',
	p: 0.1
}

const Form = (props: Props) => {
	const {
		issueId, data, initialItems, onSubmitSuccess = () => {
		}, onSubmitFailure, onClose, onEmailSendSuccess = () => {
		}
	} = props
	const { t } = useTranslation(ISSUES_FORM)
	const { emailFormik, loadingSendEmail, open, setOpen } = useEmailSO({ onEmailSendSuccess })
	const onSOSubmit = useCallback(({ values, issueId, data, response }) => {
		if (response.data && response.data.data && response.data.data?.length > 0) {
			const { id, TO, CC, Body, BCC, Subject } = response.data.data[0]
			emailFormik.setValues({
				id: id,
				to: TO ? [{ id: uuidv4(), value: TO }] : [{ id: uuidv4(), value: '' }],
				cc: CC ? [{ id: uuidv4(), value: CC }] : [{ id: uuidv4(), value: '' }],
				bcc: BCC ? [{ id: uuidv4(), value: BCC }] : [{ id: uuidv4(), value: '' }],
				body: Body,
				subject: Subject,
			})
			setOpen(true)
		}
		onSubmitSuccess({ values, issueId, data, response })
	}, [emailFormik, onSubmitSuccess, setOpen])
	const { loadingSubmit, formik } = useCustomerSOForm({ issueId, data, onSubmitSuccess: onSOSubmit, onSubmitFailure })
	const { thousandSeparator, decimalSeparator, language, dateFormat, timeFormat } = useUserData()
	const theme = useTheme()
	const isBelowMd = useMediaQuery(theme.breakpoints.down('md'));
	const displayTotals = useMemo(() => (formik.values.items?.length > 0), [formik.values.items?.length])
	const displayChargeTotal = useMemo(() => (formik.values.charges?.length > 0), [formik.values.charges?.length])
	const previousOffers = useMemo(() => data.previousOffers, [data.previousOffers])
	return (
		<FormikProvider value={formik}>
			<Box
				component={'form'}
				autoComplete="off"
				onSubmit={formik.handleSubmit}
				sx={{
					display: 'flex',
					flexDirection: 'column',
					width: '100%',
				}}
			>
				<DialogActions>
					<Button
						onClick={onClose}
						startIcon={<NavigateBefore/>}
						variant={'contained'}
						sx={{
							marginRight: 'auto'
						}}
					>
						{t('Return')}
					</Button>
					<Button
						loading={loadingSubmit}
						startIcon={<SaveIcon/>}
						color={'success'}
						variant={'contained'}
						onClick={formik.handleSubmit}
					>
						{t('Save')}
					</Button>
				</DialogActions>
				<Grid
					container
					spacing={2}
					p={1}
				>
					<Items
						formik={formik}
						initialItems={initialItems}
						theme={theme}
						IssueId={issueId}
						isBelowMd={isBelowMd}
					/>
					{displayTotals && (
						<Grid size={12} sx={{ ...totalSx }}>
							<FormLabel sx={{ pr: 1 }}>{t('Items Total')}</FormLabel>
							<FormControl {...formControlDefaults} sx={{ width: isBelowMd ? '100%' : '100px' }}>
								<NumericFormat
									{...numericFormatWithIconProps(EuroRounded)}
									thousandSeparator={thousandSeparator}
									decimalSeparator={decimalSeparator}
									name={`ItemsTotal`}
									value={calcItemsTotal(formik.values.items)}
									disabled={true}
								/>
							</FormControl>
						</Grid>
					)}
					<Charges
						formik={formik}
						theme={theme}
						IssueId={issueId}
						isBelowMd={isBelowMd}
					/>
					{displayChargeTotal && (
						<Grid size={12} sx={{ ...totalSx }}>
							<FormLabel sx={{ pr: 1 }}>{t('Items Total With Charges')}</FormLabel>
							<FormControl {...formControlDefaults} sx={{ width: isBelowMd ? '100%' : '100px' }}>
								<NumericFormat
									{...numericFormatWithIconProps(EuroRounded)}
									thousandSeparator={thousandSeparator}
									decimalSeparator={decimalSeparator}
									name={`ItemsTotalWithCharges`}
									value={calcItemsTotalWithCharges(formik.values.items, formik.values.charges)}
									disabled={true}
								/>
							</FormControl>
						</Grid>
					)}
					<Grid size={12} sx={{ display: 'flex', alignItems: 'flex-end', background: '#fefde7', p: 0.1 }}>
						<FormLabel sx={{ pr: 1 }}>{t('Contract Benefit (Markup)')}</FormLabel>
						<FormControl {...formControlDefaults} sx={{ width: isBelowMd ? '100%' : '100px' }}>
							<NumericFormat
								{...numericFormatWithIconProps(Percent)}
								thousandSeparator={thousandSeparator}
								decimalSeparator={decimalSeparator}
								name={`markup`}
								value={formik.values.markup}
								onValueChange={(values, sourceInfo) => {
									formik.setFieldValue('markup', values.floatValue)
								}}
								onBlur={formik.handleBlur}
							/>
						</FormControl>
					</Grid>
					<Grid size={12} sx={{ ...totalSx }}>
						<FormLabel sx={{ pr: 1 }}>{t('Total With Markup')}</FormLabel>
						<FormControl {...formControlDefaults} sx={{ width: isBelowMd ? '100%' : '100px' }}>
							<NumericFormat
								{...numericFormatWithIconProps(EuroRounded)}
								thousandSeparator={thousandSeparator}
								decimalSeparator={decimalSeparator}
								name={`total`}
								value={calcTotalWithMarkup(formik.values.items, formik.values.charges, formik.values.markup)}
								disabled
							/>
						</FormControl>
					</Grid>
					<Grid size={12} sx={{background: '#EDF9EB', p: 0.1 }}>
						<Box sx={{ display: 'flex', alignItems: 'flex-end'}}>
							<FormLabel
								sx={{ pr: 1 }}
								error={
									formik.touched[`discount`] &&
									Boolean(formik.errors[`discount`])
								}
							>
								{t('Discount')}
							</FormLabel>
							<FormControl {...formControlDefaults} sx={{ width: isBelowMd ? '100%' : '100px', pr: 1 }}>
								<NumericFormat
									{...numericFormatDefaults}
									thousandSeparator={thousandSeparator}
									decimalSeparator={decimalSeparator}
									name={`discount`}
									value={formik.values.discount}
									onValueChange={(values, sourceInfo) => {
										formik.setFieldValue('discount', values.floatValue)
									}}
									onBlur={() => formik.setFieldTouched('discount', true)}
								/>
							</FormControl>
							<TextField
								{...textFieldSelectDefaults}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
								value={formik.values.discountType}
								name={'discountType'}
								sx={{ width: '60px' }}
							>
								<MenuItem value={DiscountTypes.Percentage} sx={{ padding: 1 }}><Percent fontSize={'small'}/></MenuItem>
								<MenuItem value={DiscountTypes.NetValue} sx={{ padding: 1 }}><EuroRounded
									fontSize={'small'}/></MenuItem>
							</TextField>
						</Box>
						<FormHelperText>
							<ErrorMessage name={`discount`}/>
						</FormHelperText>
					</Grid>
					<Grid size={12} sx={{ ...totalSx }}>
						<FormLabel sx={{ pr: 1 }}>{t('Total With Discount')}</FormLabel>
						<FormControl {...formControlDefaults} sx={{ width: isBelowMd ? '100%' : '100px' }}>
							<NumericFormat
								{...numericFormatWithIconProps(EuroRounded)}
								thousandSeparator={thousandSeparator}
								decimalSeparator={decimalSeparator}
								name={`totalWithDiscount`}
								value={calcTotalWithDiscount(formik.values.items, formik.values.charges, formik.values.markup, {
									discount: formik.values.discount,
									discountType: formik.values.discountType
								})}
								disabled
								allowNegative
							/>
						</FormControl>
					</Grid>
					<Grid size={12}>
						<FormControl {...formControlDefaults}>
							<FormLabel>{t('Comment')}</FormLabel>
							<TextField
								{...textFieldDefaults}
								name={`comment`}
								value={formik.values.comment}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
								inputtype="textarea"
								multiline
								rows={4}
							/>
						</FormControl>
					</Grid>
				</Grid>
				{previousOffers.length > 0 && (
					<Grid size={12}>
						<FormBoxTitle title={t('Offers')}/>
						<Offers
							data={previousOffers}
							t={t}
							language={language}
							timeFormat={timeFormat}
							dateFormat={dateFormat}
						/>
					</Grid>
				)}
			</Box>
			<Confirmation
				open={open}
				handleDialog={() => setOpen(false)}
				yesBtnText={t('Send')}
				loading={loadingSendEmail}
				title={t('Email Offer')}
				maxWidth={'lg'}
				onYes={emailFormik.handleSubmit}
				onNo={() => setOpen(false)}
			>
				<SendSOEmail
					emailFormik={emailFormik}
				/>
			</Confirmation>
		</FormikProvider>
	);
};

export default Form